/* global BigInt */

const MONEY_FIELDS = new Set([
  'money', 'money1', 'money2', 'amount', 'charge', 'fee', 'price',
  'income', 'day_income', 'month_income', 'year_income', 'reward_amount',
  'prize_amount', 'entry_amount', 'balance_before', 'balance_after', 'before',
  'revenue', 'baoku_money', 'frozenmoney', 'availmoney', 'basemoney',
  'sum_money', 'on_money', 'on_apr_money', 'min_cash', 'min_recharge',
  'max_cash', 'maxm', 'sinvest', 'scash', 'invest', 'treasure', 'prizeamount',
  'funds', 'mymoney', 'yesterday', 'today', 'total', 'week', 'month',
  'subordinate', 'recommended', 'teamfee', 'teamfeea', 'teamfeeb', 'teamfeec',
  'allfee', 'allteamfee', 'countCommission', 'invest_sum', 'share_reward',
  'all_lixi', 'myRecharge', 'mining_reward', 'activityIncome', 'jichu',
  'mininvest', 'actual_amount', 'totalmoney', 'ok_apr_money',
  'tfee', 'tteamfee', 'orderprice', 'pay1', 'pay2', 'reward', 'min', 'max'
]);

export function getMoneyPrecision() {
  const value = Number(localStorage.getItem('moneyPrecision') || 2);
  return Math.max(0, Math.min(6, Number.isFinite(value) ? Math.floor(value) : 2));
}

export function formatMoney(value, precision = getMoneyPrecision()) {
  const requested = Number(precision);
  const scale = Math.max(0, Math.min(6, Number.isFinite(requested) ? Math.floor(requested) : getMoneyPrecision()));
  let text = String(value == null || value === '' ? '0' : value).trim();
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(text)) return '0' + (scale ? '.' + '0'.repeat(scale) : '');
  const negative = text.charAt(0) === '-';
  text = text.replace(/^[+-]/, '');
  const parts = text.split('.');
  let integer = (parts[0].replace(/^0+(?=\d)/, '') || '0');
  let fraction = parts[1] || '';
  const nextDigit = fraction.charAt(scale) || '0';
  fraction = fraction.slice(0, scale).padEnd(scale, '0');
  if (nextDigit >= '5') {
    let digits = integer + fraction;
    let carry = 1;
    let result = '';
    for (let i = digits.length - 1; i >= 0; i -= 1) {
      const sum = Number(digits.charAt(i)) + carry;
      result = String(sum % 10) + result;
      carry = sum >= 10 ? 1 : 0;
    }
    digits = (carry ? '1' : '') + result;
    if (scale) {
      integer = digits.slice(0, -scale) || '0';
      fraction = digits.slice(-scale);
    } else {
      integer = digits;
    }
  }
  const isZero = /^0+$/.test(integer) && (!scale || /^0+$/.test(fraction));
  return (negative && !isZero ? '-' : '') + integer + (scale ? '.' + fraction : '');
}

export function compareMoney(left, right, precision = getMoneyPrecision()) {
  const normalizeForCompare = (value) => {
    const text = formatMoney(value, precision);
    const negative = text.charAt(0) === '-';
    const digits = text.replace(/^[+-]/, '').replace('.', '').replace(/^0+/, '') || '0';
    return { negative: negative && digits !== '0', digits };
  };
  const a = normalizeForCompare(left);
  const b = normalizeForCompare(right);
  if (a.negative !== b.negative) return a.negative ? -1 : 1;
  const absolute = a.digits.length === b.digits.length ? a.digits.localeCompare(b.digits) : a.digits.length - b.digits.length;
  return a.negative ? -absolute : absolute;
}

function parseDecimal(value) {
  const text = String(value == null || value === '' ? '0' : value).trim();
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(text)) return { sign: 1n, digits: 0n, scale: 0 };
  const negative = text.charAt(0) === '-';
  const raw = text.replace(/^[+-]/, '');
  const [integer, fraction = ''] = raw.split('.');
  const digits = BigInt((integer + fraction).replace(/^0+(?=\d)/, '') || '0');
  return { sign: negative ? -1n : 1n, digits, scale: fraction.length };
}

function powerOfTen(value) {
  return 10n ** BigInt(Math.max(0, value));
}

function formatScaled(sign, digits, sourceScale, precision) {
  const scale = Math.max(0, Math.min(6, Number(precision)));
  let result = digits;
  if (sourceScale > scale) {
    const factor = powerOfTen(sourceScale - scale);
    const remainder = result % factor;
    result /= factor;
    if (remainder * 2n >= factor) result += 1n;
  } else if (sourceScale < scale) {
    result *= powerOfTen(scale - sourceScale);
  }
  const raw = result.toString().padStart(scale + 1, '0');
  const integer = scale ? raw.slice(0, -scale) : raw;
  const fraction = scale ? raw.slice(-scale) : '';
  return (sign < 0n && result !== 0n ? '-' : '') + integer + (scale ? `.${fraction}` : '');
}

export function addMoney(left, right, precision = getMoneyPrecision()) {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  const scale = Math.max(a.scale, b.scale);
  const signed = a.sign * a.digits * powerOfTen(scale - a.scale) + b.sign * b.digits * powerOfTen(scale - b.scale);
  return formatScaled(signed < 0n ? -1n : 1n, signed < 0n ? -signed : signed, scale, precision);
}

export function subtractMoney(left, right, precision = getMoneyPrecision()) {
  const value = String(right == null ? '0' : right);
  return addMoney(left, value.charAt(0) === '-' ? value.slice(1) : `-${value}`, precision);
}

export function multiplyMoney(left, right, precision = getMoneyPrecision()) {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  return formatScaled(a.sign * b.sign, a.digits * b.digits, a.scale + b.scale, precision);
}

export function divideMoney(left, right, precision = getMoneyPrecision()) {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  if (b.digits === 0n) return formatMoney(0, precision);
  const workingScale = Math.max(0, Math.min(6, Number(precision))) + 1;
  const numerator = a.digits * powerOfTen(b.scale + workingScale);
  const denominator = b.digits * powerOfTen(a.scale);
  return formatScaled(a.sign * b.sign, numerator / denominator, workingScale, precision);
}

export function getChannelPrecision(channel, fallback = getMoneyPrecision()) {
  const candidate = channel && (channel.money_precision ?? channel.precision ?? channel.decimal_places);
  const value = Number(candidate);
  return Number.isFinite(value) && value >= 0 && value <= 6 ? Math.floor(value) : fallback;
}

function isPointsRecord(record) {
  return Number(record && (record.cointype ?? record.box_type)) === 2;
}

export function normalizeResponseMoney(value) {
  if (Array.isArray(value)) {
    value.forEach((item) => normalizeResponseMoney(item));
    return value;
  }
  if (!value || typeof value !== 'object') return value;
  const isPoints = isPointsRecord(value);
  Object.keys(value).forEach((key) => {
    const current = value[key];
    if (current && typeof current === 'object') {
      normalizeResponseMoney(current);
      return;
    }
    if (!isPoints && MONEY_FIELDS.has(key) && typeof current !== 'boolean') {
      value[key] = formatMoney(current);
    }
  });
  return value;
}
